import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { rpc } from '@/lib/rpc';
import { supabase } from '@/lib/supabase';
import type { Team } from '@/types/app';

export const teamKeys = {
  all: ['teams'] as const,
  list: (includeArchived: boolean) => ['teams', 'list', includeArchived] as const,
};

const TEAM_COLUMNS = 'id, name, leader_id, areas, status, created_at, updated_at';

export function useTeams(includeArchived = false) {
  return useQuery({
    queryKey: teamKeys.list(includeArchived),
    queryFn: async (): Promise<Team[]> => {
      let query = supabase.from('teams').select(TEAM_COLUMNS).order('name');
      if (!includeArchived) query = query.eq('status', 'active');

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
  });
}

export interface UpsertTeamInput {
  id?: string | null;
  name: string;
  leader_id?: string | null;
  areas?: string[];
}

/**
 * Teams are read-only from the API — INSERT and UPDATE are revoked from every client
 * role — so this goes through admin_upsert_team(), which re-checks the caller's role
 * against the profiles table (CLAUDE.md rule 4).
 */
export function useUpsertTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpsertTeamInput): Promise<Team> => {
      return await rpc<Team>('admin_upsert_team', {
        p_name: input.name,
        p_id: input.id ?? null,
        p_leader: input.leader_id ?? null,
        p_areas: input.areas ?? [],
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.all });
    },
  });
}

/** Archive, never delete (#30). Restorable by an admin. */
export function useArchiveTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await rpc('archive_record', { p_table: 'teams', p_id: id, p_reason: reason });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.all });
    },
  });
}

export function useRestoreTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      await rpc('restore_record', { p_table: 'teams', p_id: id, p_reason: reason ?? null });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.all });
    },
  });
}
